import { useDeferredValue, useMemo, useState } from 'react'
import { indexEntries, searchPhrasebook } from '@shared/phrasebook/search'
import type { PhrasebookEntry } from '@shared/types/phrasebook'
import { SpeakButtonById } from '@web/lib/audio'
import { usePhrasebook } from '@web/lib/phrasebook'

/**
 * The phrasebook.
 *
 * Designed for the moment she is standing in front of someone in Brazil and
 * needs a word *now*: search is instant and local, the essentials are one tap
 * away, and every entry can be played out loud — which is often the fastest
 * way to be understood when the pronunciation is the part she is unsure of.
 */
export function PhrasebookPage() {
  const { data, isPending, error } = usePhrasebook()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [essentialsOnly, setEssentialsOnly] = useState(false)

  // Keeps typing responsive: the input updates immediately while the filtered
  // list renders at React's leisure.
  const deferredQuery = useDeferredValue(query)

  const indexed = useMemo(() => indexEntries(data?.entries ?? []), [data])
  const results = useMemo(
    () => searchPhrasebook(indexed, { query: deferredQuery, category, essentialsOnly }),
    [indexed, deferredQuery, category, essentialsOnly],
  )

  return (
    <div className="mx-auto max-w-lg px-4 pt-[max(1rem,var(--safe-top))] pb-6">
      <header className="mb-4">
        <h1 className="font-display text-3xl font-bold text-ink-900">Phrasebook</h1>
        <p className="mt-1 text-sm text-ink-500">
          Every word you have learned, searchable. Tap any phrase to hear it.
        </p>
      </header>

      <div className="sticky top-0 z-10 -mx-4 bg-sand-50/95 px-4 pt-1 pb-3 backdrop-blur">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search in English or Portuguese…"
          aria-label="Search the phrasebook"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="min-h-12 w-full rounded-xl bg-white px-4 text-base ring-1 ring-sand-200 focus:ring-2 focus:ring-brand-600"
        />

        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
          <Chip
            active={essentialsOnly}
            onClick={() => setEssentialsOnly((v) => !v)}
            tone="gold"
          >
            ★ Essentials
          </Chip>
          <Chip active={category === null} onClick={() => setCategory(null)}>
            All
          </Chip>
          {(data?.categories ?? []).map((c) => (
            <Chip
              key={c.key}
              active={category === c.key}
              onClick={() => setCategory(category === c.key ? null : c.key)}
            >
              {c.label}
            </Chip>
          ))}
        </div>
      </div>

      {isPending && !data && <Skeleton />}

      {error && !data && (
        <p className="rounded-card bg-cream p-5 text-ink-700">
          The phrasebook could not be loaded. Check your connection and try
          again.
        </p>
      )}

      {data && (
        <>
          <p className="mb-3 text-sm text-ink-500" aria-live="polite">
            {results.length} {results.length === 1 ? 'phrase' : 'phrases'}
          </p>

          {results.length === 0 ? (
            <p className="rounded-card bg-white p-5 text-ink-700 ring-1 ring-brand-900/5">
              Nothing matches “{query}”. Try the English word instead — or fewer
              letters.
            </p>
          ) : (
            <ul className="space-y-2">
              {results.map((entry) => (
                <PhraseRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

function PhraseRow({ entry }: { entry: PhrasebookEntry }) {
  const [open, setOpen] = useState(false)
  const hasDetail = Boolean(entry.examplePt || entry.regionNote)

  return (
    <li className="rounded-card bg-white ring-1 ring-brand-900/5">
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          onClick={() => hasDetail && setOpen((v) => !v)}
          aria-expanded={hasDetail ? open : undefined}
          className="flex-1 text-left"
        >
          <span className="flex items-center gap-2">
            <span className="text-lg font-semibold text-ink-900" lang="pt-BR">
              {entry.pt}
            </span>
            {entry.tripCritical && (
              <span
                aria-label="Essential"
                title="Essential for the trip"
                className="text-gold-500"
              >
                ★
              </span>
            )}
          </span>
          <span className="block text-ink-700">{entry.en}</span>
        </button>

        {entry.audioId && (
          <SpeakButtonById mediaId={entry.audioId} label={entry.pt} size="lg" />
        )}
      </div>

      {open && hasDetail && (
        <div className="space-y-2 border-t border-sand-200 px-4 py-3">
          {entry.examplePt && (
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="text-ink-900" lang="pt-BR">
                  {entry.examplePt}
                </p>
                {entry.exampleEn && (
                  <p className="text-sm text-ink-500">{entry.exampleEn}</p>
                )}
              </div>
              {entry.exampleAudioId && (
                <SpeakButtonById
                  mediaId={entry.exampleAudioId}
                  label={entry.examplePt as string}
                  size="md"
                />
              )}
            </div>
          )}
          {entry.regionNote && (
            <p className="rounded-xl border-l-4 border-gold-500 bg-cream px-3 py-2 text-sm text-ink-700">
              {entry.regionNote}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

function Chip({
  active,
  onClick,
  children,
  tone = 'brand',
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  tone?: 'brand' | 'gold'
}) {
  const activeClass =
    tone === 'gold' ? 'bg-gold-500 text-brand-900' : 'bg-brand-600 text-white'
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-9 shrink-0 rounded-full px-3.5 text-sm font-medium whitespace-nowrap transition-colors ${
        active ? activeClass : 'bg-white text-ink-700 ring-1 ring-sand-200'
      }`}
    >
      {children}
    </button>
  )
}

function Skeleton() {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="h-20 animate-pulse rounded-card bg-sand-100" />
      ))}
    </ul>
  )
}

